import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/api/client';
import { useInventorySync } from '@/hooks/useInventorySync';
import { StockAlertsBadge } from '@/components/StockAlertsBadge';
import InventoryPanel from '@/components/InventoryPanel';
import CompliancePanel from '@/components/CompliancePanel';
import RotaPanel from '@/components/RotaPanel';
import CashUpPanel from '@/components/CashUpPanel';
import TransfersPanel from '@/components/TransfersPanel';
import ProductionPanel from '@/components/ProductionPanel';
import LetsiesListPanel from '@/components/LetsiesListPanel';
import { AnalyticsDashboard } from '@/components/AnalyticsDashboard';
import CustomerAccountManager from '@/components/CustomerAccountManager';
import RewardsManager from '@/components/RewardsManager';
import LeaveRequestsManager from '@/components/LeaveRequestsManager';
import StaffPanel from '@/components/StaffPanel';
import AttendancePanel from '@/components/AttendancePanel';
import BranchSelector from '@/components/BranchSelector';
import NotesPanel from '@/components/NotesPanel';
import CataloguePanel from '@/components/CataloguePanel';
import PrepaymentsPanel from '@/components/PrepaymentsPanel';
import DeliveriesPanel from '@/components/DeliveriesPanel';
import CratesPanel from '@/components/CratesPanel';
import CleaningPanel from '@/components/CleaningPanel';
import IngredientsPanel from '@/components/IngredientsPanel';


/**
 * Grouped the way a bakery manager thinks about the day, rather than one
 * flat row of every screen.
 */
const TAB_GROUPS = [
  {
    name: 'Trading',
    tabs: [
      { id: 'overview', label: 'Sales overview' },
      { id: 'cashup', label: 'Cash-up' },
      { id: 'cleaning', label: 'Cleaning' },
      { id: 'prepayments', label: 'Prepayments' },
      { id: 'crates', label: 'Crates' },
      { id: 'notes', label: 'Notes' },
    ],
  },
  {
    name: 'Stock & baking',
    tabs: [
      { id: 'inventory', label: 'Inventory' },
      { id: 'ingredients', label: 'Ingredients' },
      { id: 'production', label: 'Bake log' },
      { id: 'transfers', label: 'Transfers' },
      { id: 'letsies', label: "Letsie's List" },
      { id: 'deliveries', label: 'Deliveries' },
      { id: 'catalogue', label: 'Products & shops' },
    ],
  },
  {
    name: 'People',
    tabs: [
      { id: 'rota', label: 'Rota' },
      { id: 'attendance', label: 'Attendance' },
      { id: 'leave', label: 'Leave' },
      { id: 'staff', label: 'Staff' },
    ],
  },
  {
    name: 'Money & customers',
    tabs: [
      { id: 'analytics', label: 'Analytics' },
      { id: 'accounts', label: 'Accounts' },
      { id: 'rewards', label: 'Rewards' },
    ],
  },
  {
    name: 'Compliance',
    tabs: [{ id: 'compliance', label: 'Compliance' }],
  },
];

interface DashboardMetrics {
  sales: {
    today: number;
    thisWeek: number;
    byPaymentMethod: Record<string, number>;
    transactionCount: number;
    topProducts: Array<{ name: string; quantity: number; revenue: number }>;
  };
  inventory: {
    lowStockItems: Array<{ productName: string; quantityOnHand: number; lowThreshold: number }>;
    outOfStock: Array<{ productName: string }>;
  };
  compliance: {
    completionPercent: number;
    overdueTasks: number;
    expiringCertificates: number;
    pendingTraining: number;
  };
  rota: {
    shiftsToday: number;
    pendingRequests: number;
  };
}

interface StockAlert {
  productId: string;
  productName: string;
  currentStock: number;
  lowThreshold: number;
  severity: 'info' | 'warning' | 'critical';
}

export default function ManagerDashboard() {
  const { staff, logout, activeBranchId, activeBranchName } = useAuthStore();
  // Mr & Mrs Mojakhomo are VIEWER: oversight without the ability to change
  // stock, cash, staff or orders.
  const isManager = staff?.role === 'MANAGER';
  const VIEWER_TABS = ['overview', 'inventory', 'analytics', 'attendance', 'notes', 'compliance', 'rota', 'prepayments', 'crates', 'cleaning', 'ingredients'];
  const canSee = (tab: string) => isManager || VIEWER_TABS.includes(tab);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'inventory' | 'transfers' | 'production' | 'cashup' | 'letsies' | 'analytics' | 'accounts' | 'rewards' | 'leave' | 'staff' | 'attendance' | 'notes' | 'compliance' | 'rota' | 'catalogue' | 'prepayments' | 'deliveries' | 'crates' | 'cleaning' | 'ingredients'>('overview');
  const [alerts, setAlerts] = useState<Array<{ id: string; message: string; severity: string }>>([]);
  const [stockAlerts, setStockAlerts] = useState<{ critical: StockAlert[]; warning: StockAlert[] }>({
    critical: [],
    warning: [],
  });

  const handleInventoryUpdate = (item: any) => {
    setMetrics(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        inventory: {
          ...prev.inventory,
          lowStockItems: prev.inventory.lowStockItems.filter(i => i.productName !== item.productName),
          outOfStock: prev.inventory.outOfStock.filter(i => i.productName !== item.productName),
        },
      };
    });
  };

  const handleStockAlert = (alert: any) => {
    // Dedupe by message — the sync fires repeatedly for the same item and
    // the toasts were stacking faster than they cleared
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setAlerts(prev =>
      prev.some(a => a.message === alert.message) ? prev : [...prev, { id, ...alert }],
    );
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== id));
    }, 6000);
  };

  useInventorySync(activeBranchId, handleInventoryUpdate, handleStockAlert);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [activeBranchId]);

  const loadDashboardData = async () => {
    try {
      const [salesReport, inventoryReport, stockAlertsData, weekTrend] = await Promise.all([
        apiClient.getDailySalesReport(activeBranchId, new Date().toISOString().split('T')[0]),
        apiClient.getInventoryReport(activeBranchId),
        apiClient.getInventoryAlerts(activeBranchId),
        apiClient.getRevenueTrend(activeBranchId, 7).catch(() => ({ data: [] })),
      ]);

      // "This week" was hardcoded to 0 — sum the last 7 days of the trend
      const weekRows = Array.isArray(weekTrend.data)
        ? weekTrend.data
        : weekTrend.data?.trend || [];  // { trend: [{date, revenue}] }
      const thisWeekTotal = weekRows.reduce(
        (sum: number, r: any) => sum + (Number(r.total ?? r.revenue ?? 0) || 0),
        0,
      );

      if (stockAlertsData.data) {
        setStockAlerts({
          critical: stockAlertsData.data.critical || [],
          warning: stockAlertsData.data.warning || [],
        });
      }

      // Parse reports into metrics (API returns { inventory: [...], summary })
      const inventoryItems = inventoryReport.data.inventory || [];
      const lowStockItems = inventoryItems.filter(
        (item: any) => item.status === 'LOW',
      );
      const outOfStockItems = inventoryItems.filter(
        (item: any) => item.status === 'OUT_OF_STOCK',
      );

      // daily-sales returns byPaymentMethod as { count, total, vat } per method
      // and the day's figures under `totals` — not flat numbers at the root.
      const byPaymentMethod: Record<string, number> = {};
      let transactionCount = 0;
      for (const [method, v] of Object.entries<any>(salesReport.data.byPaymentMethod || {})) {
        byPaymentMethod[method] = Number(v?.total ?? v ?? 0) || 0;
        transactionCount += Number(v?.count ?? 0) || 0;
      }
      const topProducts = (salesReport.data.topProducts || []).map((p: any) => ({
        ...p,
        name: p.name ?? p.productName ?? '—',
        quantity: Number(p.quantity ?? p.unitsSold ?? 0) || 0,
        revenue: Number(p.revenue ?? p.totalRevenue ?? 0) || 0,
      }));

      setMetrics({
        sales: {
          today: Number(salesReport.data.totals?.revenue ?? salesReport.data.total ?? 0) || 0,
          transactionCount,
          thisWeek: thisWeekTotal,
          byPaymentMethod,
          topProducts,
        },
        inventory: {
          lowStockItems,
          outOfStock: outOfStockItems,
        },
        compliance: {
          completionPercent: 0,
          overdueTasks: 0,
          expiringCertificates: 0,
          pendingTraining: 0,
        },
        rota: {
          shiftsToday: 0,
          pendingRequests: 0,
        },
      });
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Alert toasts — bottom-left so they never cover the header or tabs,
          click-through except on the toast itself, and dismissable */}
      <div className="fixed bottom-4 left-4 z-40 space-y-2 pointer-events-none max-w-[calc(100vw-2rem)] sm:max-w-sm">
        {alerts.slice(-3).map(alert => (
          <div
            key={alert.id}
            className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg text-white ${
              alert.severity === 'critical' ? 'bg-red-600' : 'bg-amber-600'
            }`}
          >
            <span className="text-sm flex-1">{alert.message}</span>
            <button
              onClick={() => setAlerts(prev => prev.filter(a => a.id !== alert.id))}
              className="text-white/80 hover:text-white font-bold leading-none shrink-0"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img src="/mepco-badge.png" alt="" className="w-14 h-14 shrink-0" />
            <div>
              <h1 className="text-3xl font-bold text-amber-600 leading-tight">Manager Dashboard</h1>
              <div className="flex items-center gap-2 mt-1">
                <BranchSelector />
                <span className="text-sm text-gray-600">• {staff?.name}</span>
              </div>
            </div>
          </div>
          <button
            onClick={logout}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Navigation — grouped, because fifteen flat tabs stopped being scannable */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Areas */}
          <div className="flex gap-1 overflow-x-auto pt-2">
            {TAB_GROUPS.filter(g => g.tabs.some(t => canSee(t.id))).map(group => {
              const active = group.tabs.some(t => t.id === activeTab);
              return (
                <button
                  key={group.name}
                  onClick={() => {
                    const first = group.tabs.find(t => canSee(t.id));
                    if (first) setActiveTab(first.id as any);
                  }}
                  className={`px-4 py-2 rounded-t-lg text-sm font-semibold whitespace-nowrap transition ${
                    active
                      ? 'bg-amber-50 text-amber-800'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {group.name}
                </button>
              );
            })}
          </div>

          {/* Screens within the chosen area */}
          <div className="flex gap-5 overflow-x-auto border-t border-gray-100">
            {(TAB_GROUPS.find(g => g.tabs.some(t => t.id === activeTab)) || TAB_GROUPS[0])
              .tabs.filter(t => canSee(t.id))
              .map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-amber-500 text-amber-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
          </div>
        </div>
      </div>


      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Sales Overview */}
        {activeTab === 'overview' && metrics && (
          <div className="space-y-8">
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-gray-500 text-sm font-medium">Today's Sales</h3>
                <p className="mt-2 text-3xl font-bold text-gray-900">R {metrics.sales.today.toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-gray-500 text-sm font-medium">This Week</h3>
                <p className="mt-2 text-3xl font-bold text-gray-900">R {metrics.sales.thisWeek.toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-gray-500 text-sm font-medium">Transactions</h3>
                <p className="mt-2 text-3xl font-bold text-gray-900 tabular-nums">
                  {metrics.sales.transactionCount}
                </p>
              </div>
            </div>

            {/* Payment Methods Breakdown */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
                <h3 className="text-lg font-medium text-gray-900">Sales by payment method</h3>
                <span className="text-sm text-gray-500">today</span>
              </div>
              {(() => {
                const entries = Object.entries(metrics.sales.byPaymentMethod);
                const total = entries.reduce((sum, [, v]) => sum + (Number(v) || 0), 0);

                // Five zero rows read as a broken screen. Say plainly that
                // nothing has been rung up rather than showing empty bars.
                if (total === 0) {
                  return (
                    <div className="py-8 text-center">
                      <p className="text-gray-600 font-medium">Nothing rung up today yet</p>
                      <p className="text-sm text-gray-500 mt-1">
                        This week came to R {metrics.sales.thisWeek.toFixed(2)}.
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {entries
                      .filter(([, v]) => (Number(v) || 0) > 0)
                      .sort((a, b) => Number(b[1]) - Number(a[1]))
                      .map(([method, amount]) => {
                        const value = Number(amount) || 0;
                        // Guard the divide — total is never 0 here, but a NaN
                        // width silently renders as a full bar, which is worse
                        // than no bar at all.
                        const share = total > 0 ? (value / total) * 100 : 0;
                        return (
                          <div key={method} className="flex justify-between items-center gap-4">
                            <span className="text-gray-700 capitalize">{method.toLowerCase()}</span>
                            <div className="flex items-center gap-4">
                              <div className="w-32 bg-gray-200 rounded-full h-2 overflow-hidden">
                                <div className="bg-amber-500 h-2 rounded-full" style={{ width: `${share}%` }} />
                              </div>
                              <span className="font-medium text-gray-900 w-24 text-right tabular-nums">
                                R {value.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
              })()}
            </div>

            {/* Top Products */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Selling Products</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Product</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-900">Qty</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-900">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.sales.topProducts.map((product, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-900">{product.name}</td>
                        <td className="py-3 px-4 text-right text-gray-600">{product.quantity}</td>
                        <td className="py-3 px-4 text-right font-medium text-gray-900">R {product.revenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <div className="space-y-6">
            <StockAlertsBadge critical={stockAlerts.critical} warning={stockAlerts.warning} />
            <InventoryPanel branchId={activeBranchId} branchName={activeBranchName} />
          </div>
        )}

        {activeTab === 'transfers' && (
          <TransfersPanel branchId={activeBranchId} branchName={activeBranchName} staffName={staff?.name} />
        )}

        {activeTab === 'production' && (
          <ProductionPanel branchId={activeBranchId} branchName={activeBranchName} />
        )}

        {/* Cash-up Tab */}
        {activeTab === 'cashup' && (
          <CashUpPanel branchId={activeBranchId} branchName={activeBranchName} />
        )}

        {activeTab === 'letsies' && <LetsiesListPanel branchName={activeBranchName} />}

        {activeTab === 'analytics' && <AnalyticsDashboard branchId={activeBranchId} />}
        {activeTab === 'accounts' && <CustomerAccountManager />}
        {activeTab === 'rewards' && <RewardsManager />}
        {activeTab === 'leave' && <LeaveRequestsManager />}

        {activeTab === 'staff' && (
          <StaffPanel branchId={activeBranchId} isManager={staff?.role === 'MANAGER'} />
        )}

        {activeTab === 'attendance' && (
          <AttendancePanel branchId={activeBranchId} branchName={activeBranchName} />
        )}

        {activeTab === 'catalogue' && <CataloguePanel activeBranchId={activeBranchId} />}

        {activeTab === 'deliveries' && <DeliveriesPanel isManager={isManager} />}

        {activeTab === 'ingredients' && (
          <IngredientsPanel
            branchId={activeBranchId}
            branchName={activeBranchName}
            isManager={isManager}
          />
        )}

        {activeTab === 'cleaning' && (
          <CleaningPanel
            branchId={activeBranchId}
            branchName={activeBranchName}
            isManager={isManager}
          />
        )}

        {activeTab === 'crates' && (
          <CratesPanel branchId={activeBranchId} branchName={activeBranchName} />
        )}

        {activeTab === 'prepayments' && (
          <PrepaymentsPanel
            branchId={activeBranchId}
            branchName={activeBranchName}
            isManager={isManager}
          />
        )}

        {activeTab === 'notes' && (
          <NotesPanel branchId={activeBranchId} branchName={activeBranchName} />
        )}

        {/* Compliance Tab */}
        {activeTab === 'compliance' && (
          <CompliancePanel branchId={activeBranchId} staffId={staff?.id} />
        )}

        {/* Rota Tab */}
        {activeTab === 'rota' && (
          <RotaPanel branchId={activeBranchId} branchName={activeBranchName} />
        )}
      </div>
    </div>
  );
}
